import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';
import { strings } from '/imports/strings';
import { formatCalendarDate } from '/imports/utils';
import { css } from '/imports/ui/css';
import { ProjectManagementPipelineRow } from '/imports/api/projectManagement';
import { ProjectManagementLifecycle } from '/imports/api/projectManagementLifecycles';

const healthColor: { [k: string]: string } = { green: css.green, yellow: css.orange, red: css.red2 };
const LABEL_W = '13rem';
// Room for "Oct 26" plus breathing space, which is what decides how many labels the axis can hold.
const TICK_MIN_PX = 72;

const GanttStyled = styled.div`
  height: 100%;
  overflow: auto;
  .gantt { min-width: 48rem; }
  .head { display: flex; position: sticky; top: 0; background: ${css.white}; z-index: 2; border-bottom: ${css.inputBorder}; }
  .label-col { width: ${LABEL_W}; min-width: ${LABEL_W}; box-sizing: border-box; padding: 0.4rem 0.6rem; border-right: ${css.inputBorder}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  /* overflow: hidden so a label can never paint over the divider the label column's border draws. The
     end ticks are anchored rather than centred, so in practice there is nothing to clip. */
  .head-track { position: relative; flex: 1; height: 1.5rem; overflow: hidden; }
  .tick { position: absolute; top: 0.2rem; font-size: 0.72rem; color: ${css.grey3}; white-space: nowrap; }
  .tick.mid { transform: translateX(-50%); }
  .tick.last { transform: translateX(-100%); }
  .body { position: relative; }
  .grid-overlay { position: absolute; left: ${LABEL_W}; right: 0; top: 0; bottom: 0; pointer-events: none; }
  .grid { position: absolute; top: 0; bottom: 0; width: 1px; background: ${css.grey7}; }
  .today { position: absolute; top: 0; bottom: 0; width: 2px; background: ${css.primaryColor}; opacity: 0.5; }
  .row { display: flex; border-bottom: 1px solid ${css.grey7}; }
  .row .label-col { cursor: pointer; color: ${css.primaryColor}; font-weight: 600; }
  .track { position: relative; flex: 1; height: 2.3rem; }
  .span { position: absolute; top: 50%; height: 4px; transform: translateY(-50%); background: ${css.grey6}; border-radius: 2px; }
  .connector { position: absolute; top: 50%; height: 2px; transform: translateY(-50%); background: ${css.grey5}; }
  .ms { position: absolute; top: 50%; width: 0.7rem; height: 0.7rem; transform: translate(-50%, -50%) rotate(45deg); }
  .ms.planned { border: 1.5px solid ${css.grey3}; background: ${css.white}; }
  .ms.planned.pay { border-color: ${css.primaryColor}; border-width: 2px; }
  /* Tasks: thin vertical lines (vs the milestone diamonds). Default grey = estimated; actual overrides colour inline. */
  .task-line { position: absolute; top: 14%; bottom: 14%; width: 3px; transform: translateX(-50%); border-radius: 1px; background: ${css.grey4}; }
  .empty { padding: 3rem; text-align: center; color: ${css.grey4}; }
`;

interface Props {
  rows: ProjectManagementPipelineRow[];
  lifecycle: ProjectManagementLifecycle;
}

interface GanttItem { _id: string; kind: 'milestone' | 'task'; label: string; planned?: Date; actual?: Date; pay: boolean; color: string; }

const ms = (d?: Date) => (d ? new Date(d).getTime() : undefined);
const dayMs = 86400000;

/** Timeline/Gantt view: a row per project on a shared month axis, plotting BOTH milestones — as planned
 *  (hollow ◇) / actual (filled ◆) diamonds, payment triggers accent-outlined and actuals health-colored —
 *  AND tasks, as thin vertical lines at their estimated / actual dates. Same for every user. */
export const ProjectManagementGanttView = ({ rows }: Props) => {
  const navigate = useNavigate();
  // Each row plots its milestones (diamonds) and its tasks (vertical lines), normalized to one shape.
  const itemsOf = (r: ProjectManagementPipelineRow): GanttItem[] => [
    ...r.milestones.map((m) => ({ _id: m._id, kind: 'milestone' as const, label: m.label, planned: m.plannedDate, actual: m.actualDate, pay: !!m.isPaymentTrigger, color: healthColor[m.health || 'green'] })),
    ...(r.tasks || []).map((t) => ({ _id: t._id, kind: 'task' as const, label: t.label, planned: t.estimatedDate, actual: t.actualDate, pay: false, color: css.green })),
  ];

  // The axis thins itself to the width it actually has, so the same portfolio reads on a laptop and on a
  // wide monitor. Measured rather than assumed: the track is a flex child of an inner scroller.
  const trackRef = useRef<HTMLDivElement>(null);
  const [trackWidth, setTrackWidth] = useState(0);
  useEffect(() => {
    const el = trackRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(([entry]) => setTrackWidth(entry.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const times: number[] = [];
  for (const r of rows) for (const it of itemsOf(r)) { const p = ms(it.planned); const a = ms(it.actual); if (p) times.push(p); if (a) times.push(a); }
  if (!times.length) return <GanttStyled><div className="empty">—</div></GanttStyled>;

  const minT = Math.min(...times);
  const maxT = Math.max(...times);
  const pad = Math.max((maxT - minT) * 0.05, 7 * dayMs);
  const start = minT - pad;
  const end = maxT + pad;
  const pct = (t: number) => ((t - start) / (end - start)) * 100;

  // Every month boundary in range, before thinning. Starts at the first boundary AT OR AFTER the axis
  // start: flooring to the 1st lands before it, and a tick at a negative offset is drawn to the left of
  // the track — over the divider and the project names.
  const months: Date[] = [];
  let cursor = new Date(new Date(start).getFullYear(), new Date(start).getMonth(), 1);
  if (cursor.getTime() < start) cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
  const endDate = new Date(end);
  while (cursor <= endDate && months.length < 600) {
    months.push(new Date(cursor));
    cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
  }
  // Thin them to what the track can actually hold. A portfolio spanning years produced a label per month
  // on top of its neighbours; the step is whatever keeps roughly TICK_MIN_PX between labels, rounded up to
  // a interval that reads as one (quarter, half-year, year) rather than an arbitrary run of months.
  const maxTicks = Math.max(2, Math.floor((trackWidth || 900) / TICK_MIN_PX));
  const rawStep = Math.ceil(months.length / maxTicks);
  const step = [1, 2, 3, 6, 12, 24, 60].find((n) => n >= rawStep) || Math.ceil(rawStep / 12) * 12;
  const ticks = months.filter((_, i) => i % step === 0);
  // A step of a year or more has nothing useful to say about the month.
  const tickLabel = (t: Date) => (step >= 12
    ? String(t.getFullYear())
    : t.toLocaleDateString(undefined, { month: 'short', year: '2-digit' }));
  const today = Date.now();

  return (
    <GanttStyled>
      <div className="gantt">
        <div className="head">
          <div className="label-col" />
          <div className="head-track" ref={trackRef}>
            {
              ticks.map((t, i) => (
                // The first label starts at the edge and the last ends at it; centring either would hang
                // half of it outside the track — over the divider on the left, off the view on the right.
                <div
                  className={`tick${i === 0 ? '' : (i === ticks.length - 1 ? ' last' : ' mid')}`}
                  key={t.getTime()}
                  style={{ left: `${pct(t.getTime())}%` }}
                >
                  {tickLabel(t)}
                </div>
              ))
            }
          </div>
        </div>
        <div className="body">
          <div className="grid-overlay">
            {/* The grid follows the labels: a line per month under quarterly labels reads as noise. */}
            {ticks.map((t) => <div className="grid" key={t.getTime()} style={{ left: `${pct(t.getTime())}%` }} />)}
            {today >= start && today <= end && <div className="today" style={{ left: `${pct(today)}%` }} />}
          </div>
          {
            rows.map((r) => {
              const dated = itemsOf(r).filter((it) => it.planned || it.actual);
              const planned = dated.map((it) => ms(it.planned)).filter((t): t is number => !!t);
              const spanL = planned.length ? pct(Math.min(...planned)) : null;
              const spanR = planned.length ? pct(Math.max(...planned)) : null;
              return (
                <div className="row" key={r.project._id}>
                  <div className="label-col" onClick={() => navigate(`/project-management/projects/${r.project._id}`)}>{r.project.name}</div>
                  <div className="track">
                    {spanL != null && spanR != null && <div className="span" style={{ left: `${spanL}%`, width: `${Math.max(0, spanR - spanL)}%` }} />}
                    {
                      dated.map((it) => {
                        const p = ms(it.planned);
                        const a = ms(it.actual);
                        const title = [
                          it.label,
                          p ? `${strings.Estimated()} ${formatCalendarDate(it.planned as Date)}` : '',
                          a ? `${strings.Actual()} ${formatCalendarDate(it.actual as Date)}` : '',
                        ].filter(Boolean).join(' · ');
                        const pp = p != null ? pct(p) : null;
                        const ap = a != null ? pct(a) : null;
                        if (it.kind === 'task') {
                          // Tasks are thin vertical lines: grey at the estimated date, coloured at the actual.
                          return (
                            <React.Fragment key={it._id}>
                              {pp != null && <div className="task-line" style={{ left: `${pp}%` }} data-tooltip-id="pm-gantt-tip" data-tooltip-content={title} />}
                              {ap != null && <div className="task-line" style={{ left: `${ap}%`, background: it.color }} data-tooltip-id="pm-gantt-tip" data-tooltip-content={title} />}
                            </React.Fragment>
                          );
                        }
                        return (
                          <React.Fragment key={it._id}>
                            {pp != null && ap != null && <div className="connector" style={{ left: `${Math.min(pp, ap)}%`, width: `${Math.abs(ap - pp)}%` }} />}
                            {pp != null && <div className={`ms planned${it.pay ? ' pay' : ''}`} style={{ left: `${pp}%` }} data-tooltip-id="pm-gantt-tip" data-tooltip-content={title} />}
                            {ap != null && <div className="ms actual" style={{ left: `${ap}%`, background: it.color }} data-tooltip-id="pm-gantt-tip" data-tooltip-content={title} />}
                          </React.Fragment>
                        );
                      })
                    }
                  </div>
                </div>
              );
            })
          }
        </div>
      </div>
      {/* One shared tooltip for every marker — anchors reference it by id (react-tooltip, not native title). */}
      <Tooltip id="pm-gantt-tip" />
    </GanttStyled>
  );
};
